import React, { useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../window';
import { NotificationContext } from '../context/NotificationContext';
import { getCurrentReturnRoute } from '../context/PendingPurchaseContext';
import { useTranslation } from '../i18n';
import { fetchOneTapOffer, OneTapOffer, startOneTap } from './oneTap';
import { safeLocalStorage } from '../utils/storage';

/**
 * The member's one-tap offer while `enabled` (a paywall is showing). Members
 * who never paid are not asked: they always get null without a request.
 */
export const useOneTapOffer = (enabled: boolean): { offer: OneTapOffer | null; loading: boolean } => {
  const { user } = useContext(AppContext);
  const hasPaidBefore = Boolean(user?.credentials?.lastTransaction);
  const [offer, setOffer] = useState<OneTapOffer | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !hasPaidBefore) {
      setOffer(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    fetchOneTapOffer(controller.signal).then((result) => {
      if (!controller.signal.aborted) {
        setOffer(result);
        setLoading(false);
      }
    });
    return () => controller.abort();
  }, [enabled, hasPaidBefore]);

  return { offer, loading };
};

/**
 * Starts `offer` and leaves for the payment provider; a refusal (the offer
 * changed since it was shown) is notified and sends the member to the payment
 * page through `onOtherOptions`.
 */
export const useStartOneTap = (onOtherOptions: () => void) => {
  const { t } = useTranslation();
  const { addNotification } = useContext(NotificationContext);
  const [starting, setStarting] = useState(false);

  const start = async (offer: OneTapOffer) => {
    setStarting(true);
    // PaymentReturn's success modal reads it, as after a payment-page start (bonus included)
    safeLocalStorage.setItem('purchasedCredits', String(offer.credits + (offer.bonusCredits ?? 0)));
    const result = await startOneTap(offer, getCurrentReturnRoute());
    if ('redirectUrl' in result) {
      window.location.href = result.redirectUrl;
      return;
    }
    setStarting(false);
    const messages = result.errors.length ? result.errors : [t('payment.payment_failed_try_again')];
    messages.forEach((message) => addNotification({ message, level: 'danger', dismissible: true }));
    onOtherOptions();
  };

  return { start, starting };
};

interface OneTapButtonsProps {
  /** Whether the paywall is showing; the offer is fetched only then */
  show?: boolean;
  /** An offer fetched already (null: none); fetched here when omitted */
  offer?: OneTapOffer | null;
  /** Opens the payment page (close the paywall first) */
  onOtherOptions: () => void;
  /** Label of the single payment-page button shown without an offer */
  fallbackLabel?: string;
  className?: string;
}

/**
 * A paywall's way to buy: for a member who has paid before and whose last
 * package and method are still offered, "Pay <amount> with <method>" (one tap,
 * straight to the provider) and "Other options" (the payment page), with the
 * bonus coupon's `+N bonus credits` above them as on the payment page; for
 * everybody else, and while the offer loads, the single payment-page button.
 */
const OneTapButtons: React.FC<OneTapButtonsProps> = ({ show = true, offer: given, onOtherOptions, fallbackLabel, className = '' }) => {
  const { t } = useTranslation();
  const fetched = useOneTapOffer(show && given === undefined);
  const offer = given === undefined ? fetched.offer : given;
  const { start, starting } = useStartOneTap(onOtherOptions);

  if (!offer) {
    return (
      <Button variant="primary" className={className} onClick={onOtherOptions} data-one-tap="fallback">
        {fallbackLabel ?? t('profile.go_to_payment')}
      </Button>
    );
  }

  return (
    <div className={`one-tap-buttons d-grid gap-2 ${className}`}>
      {offer.bonusCredits > 0 && (
        <div className="one-tap-bonus text-success fw-semibold" data-bonus-credits={offer.bonusCredits}>
          {t('payment.coupon_bonus_credits', { credits: offer.bonusCredits })}
        </div>
      )}
      <Button
        variant="primary"
        onClick={() => start(offer)}
        disabled={starting}
        data-one-tap="pay"
      >
        {starting && <Spinner animation="border" size="sm" className="me-2" />}
        {t('payment.one_tap_pay', { amount: offer.prettyPrice, method: offer.methodName })}
      </Button>
      <Button variant="outline-secondary" onClick={onOtherOptions} disabled={starting} data-one-tap="other">
        {t('payment.other_options')}
      </Button>
    </div>
  );
};

export default OneTapButtons;
